import React, {  useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Checkbox,
  Select,
  MenuItem,
  Card,
  Box,
  Typography,
  IconButton,
  TableContainer,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import DecimalTextField from '../../../../components/common/DecimalTextField';
import { useFormikContext, FieldArray } from 'formik';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { ReactComponent as DeleteIcon } from '../../../../assets/SVG/trash.svg';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import TaxBreakdownPopover from './TaxBreakdownPopover';
import { PackageHeaderSummary, PackageServiceRow } from './PackageServicesModal';
import { Service } from './newInvoice.types';
import { CHECKED_ICON, UNCHECKED_ICON } from '../common/InvoiceTableCommon';
import useCurrency from '../../../../components/CurrencyProvider';
import { formatDateDMY } from '../../../../utils/dateUtils';

export interface ServicesListProps {
  services: Service[];
  onSelectChange: (idx: number) => void;
  onRemoveService?: (idx: number) => void;
  packageErrors?: boolean[];
  packageOptions?: string[]; // names to show in Package dropdown
  onPackageChange?: (idx: number, pkgName: string) => void;
  packageServicesRows?: PackageServiceRow[];
  packageHeaderSummary?: PackageHeaderSummary;
  providerOptions?: { value: string; label: string }[];
  onProviderChange?: (idx: number, providerId: string) => void;
  onPackageInfoClick?: (idx: number) => void; // <-- NEW: parent handles icon click
  // NEW: parent can pass a handler to add selected package services into `services`
  onAddServicesFromPackage?: (rows: PackageServiceRow[]) => void;
  /** When true, charges field becomes editable (used for package close flow) */
  isClosePackageMode?: boolean;
}

// helper fns outside component – pure and reusable
const computePercent = (amount: number, base: number) =>
  base > 0 ? parseFloat(((amount / base) * 100).toFixed(2)) : 0;

const computeAmount = (percent: number, base: number) =>
  parseFloat(((percent / 100) * base).toFixed(2));

// NEW: clamp percent between 0 and 100
const clampPercent = (value: number) => {
  if (Number.isNaN(value)) return 0;
  if (value < 0) return 0;
  if (value > 100) return 100;
  return value;
};

// shared base style for all input roots (same borderRadius everywhere)
const commonInputRootSx = {
  height: 40,
  borderRadius: 2, // <- same radius for Charges, Net, Concession, Tax
  fontSize: 13,
};

// shared style for Amount / % MUI TextFields (re-using common radius)
const amountPercentFieldSx = {
  '& .MuiOutlinedInput-root': {
    ...commonInputRootSx,
    backgroundColor: '#FFFFFF',
    paddingX: 1,
    '& fieldset': {
      borderColor: '#E4E7EC',
    },
    '&:hover fieldset': {
      borderColor: '#D0D5DD',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#5A8AEA',
      boxShadow: '0 0 0 1px rgba(90, 138, 234, 0.15)',
    },
    '& input::placeholder': {
      color: '#98A2B3',
      opacity: 1,
    },
  },
  '& .MuiInputAdornment-root': {
    color: '#667085',
    '& .MuiTypography-root, & p': {
      fontSize: 11,
    },
  },
};

// amount field: fixed but narrower (~140px)
const amountFieldSx = {
  ...amountPercentFieldSx,
  flex: '0 0 125px',
  maxWidth: 130,
};

// percent field: slightly wider now (~90px)
const percentFieldSx = {
  ...amountPercentFieldSx,
  flex: '0 0 100px',
  maxWidth: 100,
};

const ServicesList: React.FC<ServicesListProps> = ({
  services,
  onSelectChange,
  onRemoveService,
  packageErrors,
  packageOptions = [],
  onPackageChange,
  providerOptions,
  onProviderChange,
  onPackageInfoClick,
  onAddServicesFromPackage,
  isClosePackageMode = false,
}) => {
  const [taxAnchorEl, setTaxAnchorEl] = useState<HTMLElement | null>(null);
  const [taxOpen, setTaxOpen] = useState(false);
  const { currencySymbol, formatCurrency } = useCurrency();
  

  // eslint-disable-next-line no-console
  console.log('onAddServicesFromPackage---------', onAddServicesFromPackage, packageOptions);
  

  // pull Formik context – assumes parent <Formik initialValues={{ services }} />
  const { values, setFieldValue } = useFormikContext<{ services: Service[] }>();

  
  /* Hidden temporarily - we are doing this service configuration later */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleTaxToggle = (e: React.MouseEvent<HTMLElement>) => {
    const target = e.currentTarget as HTMLElement;
    if (taxOpen && taxAnchorEl === target) {
      setTaxOpen(false);
      setTaxAnchorEl(null);
    } else {
      setTaxAnchorEl(target);
      setTaxOpen(true);
    }
  };

  /* Hidden temporarily - we are doing this service configuration later */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleTaxClose = () => {
    setTaxOpen(false);
    setTaxAnchorEl(null);
  };

  /* Hidden temporarily - we are doing this service configuration later */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const taxItems = [
    { label: 'Sales Tax (3%)', value: '$6.00' },
    { label: 'VAT (2%)', value: '$4.00' },
  ];

  return (
    <Card
      sx={{
        borderRadius: 3,
        p: 2,
        boxShadow: 'none',
        border: '1px solid #E4E7EC',
        backgroundColor: '#FFFFFF',
      }}
    >
      <TableContainer sx={{ maxHeight: { xs: 320, md: 480 }, overflowX: 'auto' }}>
        <Table sx={{ borderCollapse: 'collapse', minWidth: 800 }}>
          <TableHead>
            <TableRow
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 1,
                background: '#F5F6F7',
                '& .MuiTableCell-root': { borderBottom: 'none', background: '#F5F6F7' },
                whiteSpace: 'nowrap',
              }}
            >
              <TableCell padding="checkbox" sx={{ borderTopLeftRadius: 12, borderBottomLeftRadius: 12 }}></TableCell>
              <TableCell>Date</TableCell>
              <TableCell sx={{ minWidth: 100, whiteSpace: 'nowrap' }}>Code</TableCell>
              <TableCell>Service</TableCell>
              <TableCell sx={{ minWidth: 250 }}>Package</TableCell>
              <TableCell><span>Provider<span style={{ color: 'red' }}> *</span></span></TableCell>
              <TableCell sx={{ width: 140 }}>Charges</TableCell>
              <TableCell sx={{ width: 320 }}>Concession Amt. | %</TableCell>
              <TableCell sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: 320 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  Tax Amt. | %
                  {/* Hidden temporarily - we are doing this service configuration later
                  <IconButton
                    onClick={handleTaxToggle}
                    size="small"
                    sx={{ p: 0, ml: 0.5, cursor: 'pointer' , color: '#5A8AEA'}}                    
                  >
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                  <TaxBreakdownPopover
                    title="Tax Amount"
                    items={taxItems}
                    anchorEl={taxAnchorEl}
                    open={taxOpen}
                    onClose={handleTaxClose}
                  />
                  */}
                </Box>
              </TableCell>
              <TableCell sx={{ width: 140 }}>
                Net Amt.
              </TableCell>
              {onRemoveService && <TableCell />}
            </TableRow>
          </TableHead>
      
          <FieldArray
            name="services"
            render={() => (
              <TableBody>
                {services.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={onRemoveService ? 11 : 10} align="center">
                      <Typography variant="body2" sx={{ py: 3, color: 'text.secondary' }}>
                        No services added yet. Use the “Search By Service” box above to add one.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  services.map((svc, idx) => {
                    const formSvc = values.services[idx] ?? svc;
                    const baseCharges = Number(formSvc.charges) || 0;

                    // always read from Formik first
                    let concessionAmount = formSvc.concession?.amount ?? 0;
                    let concessionPercent = formSvc.concession?.percent ?? 0;
                    let taxAmount = formSvc.tax?.amount ?? 0;
                    let taxPercent = formSvc.tax?.percent ?? 0;
                    let netAmount = formSvc.net ?? 0;

                    const setSvcField = (path: string, val: unknown) =>
                      setFieldValue(`services[${idx}].${path}`, val);

                    // ---- ONE-TIME INIT FROM API (discount/tax) ----
                    if (baseCharges > 0 && netAmount === 0) {
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      const apiSvc = svc as any;

                      // 1) Discount percent (from package_service_rates[0].discount_percent or discount_percent)
                      let apiDiscountPercent: number | undefined;
                      const apiRates = apiSvc.package_service_rates as
                        | { discount_percent?: number }[]
                        | undefined;

                      if (Array.isArray(apiRates) && typeof apiRates[0]?.discount_percent === 'number') {
                        apiDiscountPercent = apiRates[0].discount_percent;
                      } else if (typeof apiSvc.discount_percent === 'number') {
                        apiDiscountPercent = apiSvc.discount_percent;
                      }

                      if (concessionAmount === 0 && concessionPercent === 0 && apiDiscountPercent && apiDiscountPercent > 0) {
                        const seededPercent = clampPercent(apiDiscountPercent);
                        const seededAmount = computeAmount(seededPercent, baseCharges);
                        concessionPercent = seededPercent;
                        concessionAmount = seededAmount;
                        setSvcField('concession', { amount: seededAmount, percent: seededPercent });
                      }

                      // 2) Tax percent (from apiSvc.tax_percent if present, else 0)
                      if (taxAmount === 0 && taxPercent === 0 && typeof apiSvc.tax_percent === 'number') {
                        const seededTaxPercent = clampPercent(apiSvc.tax_percent);
                        const seededTaxAmount = computeAmount(seededTaxPercent, baseCharges);
                        taxPercent = seededTaxPercent;
                        taxAmount = seededTaxAmount;
                        setSvcField('tax', { amount: seededTaxAmount, percent: seededTaxPercent });
                      }

                      // 3) Net amount = charges - concession + tax
                      const seededNet = baseCharges - concessionAmount + taxAmount;
                      if (seededNet !== netAmount) {
                        netAmount = seededNet;
                        setSvcField('net', seededNet);
                      }
                    }

                    // ---- Handlers (now also update net) ----

                    // Taxable amount = charges - concession
                    const getTaxableAmount = (concession: number) => baseCharges - concession;

                    const recomputeNet = (amountConcession: number, amountTax: number) => {
                      const net = baseCharges - amountConcession + amountTax;
                      setSvcField('net', net);
                      return net;
                    };

                    // Clamp amount to not exceed base (charges or taxable amount)
                    const clampToCharges = (amount: number) => {
                      if (amount < 0) return 0;
                      if (baseCharges > 0 && amount > baseCharges) return baseCharges;
                      return amount;
                    };

                    const clampToTaxable = (amount: number, taxableBase: number) => {
                      if (amount < 0) return 0;
                      if (taxableBase > 0 && amount > taxableBase) return taxableBase;
                      return amount;
                    };

                    const handleConcessionAmountChange = (rawAmount: number) => {
                      let amount = rawAmount;
                      // Clamp amount to not exceed charges
                      amount = clampToCharges(amount);
                      const percent = clampPercent(computePercent(amount, baseCharges));
                      setSvcField('concession', { amount, percent });
                      
                      // Recalculate tax based on new taxable amount
                      const newTaxableAmount = getTaxableAmount(amount);
                      const newTaxAmount = computeAmount(taxPercent, newTaxableAmount);
                      setSvcField('tax', { amount: newTaxAmount, percent: taxPercent });
                      
                      recomputeNet(amount, newTaxAmount);
                    };

                    const handleConcessionPercentChange = (rawPercent: number) => {
                      const percent = clampPercent(rawPercent);
                      let amount = computeAmount(percent, baseCharges);
                      // Clamp amount to not exceed charges
                      amount = clampToCharges(amount);
                      setSvcField('concession', { amount, percent });
                      
                      // Recalculate tax based on new taxable amount
                      const newTaxableAmount = getTaxableAmount(amount);
                      const newTaxAmount = computeAmount(taxPercent, newTaxableAmount);
                      setSvcField('tax', { amount: newTaxAmount, percent: taxPercent });
                      
                      recomputeNet(amount, newTaxAmount);
                    };

                    const handleTaxAmountChange = (rawAmount: number) => {
                      let amount = rawAmount;
                      const taxableAmount = getTaxableAmount(concessionAmount);
                      // Clamp amount to not exceed taxable amount
                      amount = clampToTaxable(amount, taxableAmount);
                      const percent = clampPercent(computePercent(amount, taxableAmount));
                      setSvcField('tax', { amount, percent });
                      recomputeNet(concessionAmount, amount);
                    };

                    const handleTaxPercentChange = (rawPercent: number) => {
                      const percent = clampPercent(rawPercent);
                      const taxableAmount = getTaxableAmount(concessionAmount);
                      let amount = computeAmount(percent, taxableAmount);
                      // Clamp amount to not exceed taxable amount
                      amount = clampToTaxable(amount, taxableAmount);
                      setSvcField('tax', { amount, percent });
                      recomputeNet(concessionAmount, amount);
                    };

                    return (
                      <TableRow
                        key={idx}
                        sx={{
                          background: '#FFFFFF',
                          '&:hover': {
                            backgroundColor: '#F9FAFB',
                          },
                          '& td': {
                            borderTop: idx === 0 ? 'none' : undefined,
                            borderBottom:
                              idx === services.length - 1 ? 'none' : undefined,
                            paddingY: 1.25,
                          },
                        }}
                      >
                        <TableCell padding="checkbox" sx={{ borderTopLeftRadius: 12, borderBottomLeftRadius: 12 }}>
                          <Checkbox
                            checked={svc.checked}
                            onChange={() => onSelectChange(idx)}
                            icon={UNCHECKED_ICON}
                            checkedIcon={CHECKED_ICON}
                            disableRipple
                          />
                        </TableCell>
                        <TableCell sx={{ width: 180, whiteSpace: 'nowrap' }}>{svc.date ? formatDateDMY(svc.date) : '-'}</TableCell>
                        <TableCell sx={{ minWidth: 100, whiteSpace: 'nowrap' }}>{svc.code}</TableCell>
                        <TableCell sx={{ width: 200 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap', minWidth: 0 }}>
                            <Typography variant="body2" noWrap sx={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis' }}>{svc.service}</Typography>
                            {svc.prescribe_from_doctor && (
                              <Tooltip title="Prescription" arrow>
                                <Box
                                  component="span"
                                  sx={{                                    
                                    fontFamily: 'Montserrat, sans-serif',
                                    fontWeight: 600,
                                    fontSize: 11,
                                    color: '#2E7D32',
                                    bgcolor: '#E8F5E9',
                                    px: 1.5,
                                    py: 0.5,
                                    borderRadius: 2,
                                  }}
                                >
                                  Rx 
                                </Box>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>

                        <TableCell sx={{ minWidth: 250 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            {/**
                             * Package dropdown options:
                             * - Prefer per-service packages from API (svc.package_service_rates)
                             * - Fallback to global packageOptions when none are present
                             */}
                            {(() => {
                              const svcWithPackages = svc as Service;
                              const hasPerServicePackages = Array.isArray(svcWithPackages.package_service_rates) &&
                                svcWithPackages.package_service_rates.length > 0;

                              // NOTE:
                              // If this service does not have any `package_service_rates` from API,
                              // we deliberately DISABLE the package selection for this row.
                              // Reason: backend did not configure package pricing for this service,
                              // so allowing manual selection could create inconsistent billing.

                              const rowPackageOptions = hasPerServicePackages
                                ? svcWithPackages.package_service_rates!
                                  .map((r) => r.name)
                                  .filter((name) => !!name)
                                : [];

                              const isPackageSelectDisabled = !hasPerServicePackages;

                              return (
                                <>
                                  <Select
                                    value={svc.package || ''}
                                    size="small"
                                    displayEmpty
                                    disabled={isPackageSelectDisabled}
                                    onChange={(e) => onPackageChange?.(idx, e.target.value as string)}
                                    error={!!packageErrors && !!packageErrors[idx]}
                                    sx={{
                                      width: 250, // same width for selected / non-selected
                                      '& .MuiOutlinedInput-notchedOutline': {
                                        borderRadius: 3,
                                        borderColor: '#E4E7EC',
                                      },
                                      '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#D0D5DD',
                                      },
                                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#5A8AEA',
                                        boxShadow: '0 0 0 1px rgba(90, 138, 234, 0.15)',
                                      },
                                      '& .MuiSelect-select': {
                                        paddingY: 1,
                                        paddingX: 1.5,
                                        fontSize: 13,
                                        display: 'flex',
                                        alignItems: 'center',
                                      },
                                    }}
                                  >
                                    <MenuItem value="">
                                      <Typography variant="body2" color="text.secondary">
                                        {isPackageSelectDisabled
                                          ? 'No packages available'
                                          : 'Select package'}
                                      </Typography>
                                    </MenuItem>
                                    {rowPackageOptions.length === 0 ? (
                                      // If this row came from PackageServicesModal, it already has a
                                      // package name (svc.package) but no per-service package_service_rates.
                                      // In that case, show that single package as the only option so the
                                      // disabled select still displays the correct label.
                                      isPackageSelectDisabled && svc.package ? (
                                        <MenuItem value={svc.package}>
                                          <Typography variant="body2">{svc.package}</Typography>
                                        </MenuItem>
                                      ) : null
                                    ) : (
                                      rowPackageOptions.map((name) => (
                                        <MenuItem key={name} value={name}>
                                          <Typography variant="body2">{name}</Typography>
                                        </MenuItem>
                                      ))
                                    )}
                                  </Select>

                                  <IconButton
                                    size="small"
                                    sx={{
                                      p: 0,
                                      ml: 0.5,
                                      color: svc.package && !isPackageSelectDisabled ? '#5A8AEA' : 'text.disabled',
                                      cursor: svc.package && !isPackageSelectDisabled ? 'pointer' : 'default',
                                    }}
                                    disabled={!svc.package || isPackageSelectDisabled}
                                    onClick={() => {
                                      if (!svc.package || isPackageSelectDisabled) return;
                                      // debug logs
                                      // eslint-disable-next-line no-console
                                      console.log('Package info click idx:', idx);
                                      // eslint-disable-next-line no-console
                                      console.log('Package id at idx:', services[idx]?.package_id);
                                      onPackageInfoClick?.(idx); // parent receives idx
                                    }}
                                  >
                                    <InfoOutlinedIcon fontSize="small" />
                                  </IconButton>
                                </>
                              );
                            })()}
                          </Box>
                        </TableCell>
                        
                        <TableCell>
                          <Select
                            value={svc.provider || ''}
                            size="small"
                            sx={{ minWidth: 160, borderRadius: 2 }}
                            displayEmpty
                            error={svc.checked && !svc.provider}
                            onChange={(e) => onProviderChange?.(idx, e.target.value as string)}
                          >
                            <MenuItem value="">
                              <Typography variant="body2" color="text.secondary">
                                Select doctor
                              </Typography>
                            </MenuItem>
                            {(providerOptions ?? []).map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                <Typography variant="body2">{opt.label}</Typography>
                              </MenuItem>
                            ))}
                          </Select>
                        </TableCell>

                        {/* Charges – editable if isFromSearch (and NOT from a package) or isClosePackageMode, otherwise read-only */}
                        <TableCell>
                          {((svc.isFromSearch && !svc.is_from_package) || isClosePackageMode) ? (
                            <DecimalTextField
                              size="small"
                              numericValue={Number(formSvc.charges) || 0}
                              onNumericChange={(newCharges) => {
                                setSvcField('charges', newCharges);
                                // Recompute net when charges change 
                                const newNet = newCharges - concessionAmount + taxAmount;
                                setSvcField('net', newNet);
                              }}
                              max={9999999.99}
                              placeholder="Amount"
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">{currencySymbol}</InputAdornment>
                                ),
                              }}
                              sx={{
                                ...amountPercentFieldSx,
                                minWidth: 100,
                                width: 110,
                              }}
                            />
                          ) : (
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                color: '#344054',
                                fontSize: 14,
                                textAlign: 'left',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {formatCurrency(Number(formSvc.charges) || 0)}
                            </Typography>
                          )}
                        </TableCell>

                        {/* Concession: Amount | % – now auto-filled from API when present */}
                        <TableCell sx={{ width: 320 }}>
                          {/* Read-only display when service is from a package */}
                          {(svc.is_from_package || svc.package) ? (
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                color: '#344054',
                                fontSize: 14,
                                textAlign: 'left',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {currencySymbol} {concessionAmount.toFixed(2)}
                              <Box component="span" sx={{ mx: 1.5, color: '#98A2B3' }}>|</Box>
                              {concessionPercent.toFixed(2)}%
                            </Typography>
                          ) : (
                            <Box
                              sx={{
                                display: 'flex',
                                gap: 0.75,
                                alignItems: 'center',
                                minWidth: 0,
                              }}
                            >
                              <DecimalTextField
                                size="small"
                                numericValue={concessionAmount}
                                onNumericChange={handleConcessionAmountChange}
                                placeholder="Amount"
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">{currencySymbol}</InputAdornment>
                                  ),
                                }}
                                sx={amountFieldSx}
                              />
                              <DecimalTextField
                                size="small"
                                numericValue={concessionPercent}
                                onNumericChange={handleConcessionPercentChange}
                                placeholder="-"
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">%</InputAdornment>
                                  ),
                                }}
                                sx={percentFieldSx}
                              />
                            </Box>
                          )}
                        </TableCell>

                        {/* Tax: Amount | % – amount + slightly wider % */}
                        <TableCell sx={{ width: 320 }}>
                          {formSvc.is_from_package ? (
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                color: '#344054',
                                fontSize: 14,
                                textAlign: 'left',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {currencySymbol} {taxAmount.toFixed(2)}
                              <Box component="span" sx={{ mx: 1.5, color: '#98A2B3' }}>|</Box>
                              {taxPercent.toFixed(2)}%
                            </Typography>
                          ) : (
                            <Box
                              sx={{
                                display: 'flex',
                                gap: 0.75,
                                alignItems: 'center',
                                minWidth: 0,
                              }}
                            >
                              <DecimalTextField
                                size="small"
                                numericValue={taxAmount}
                                onNumericChange={handleTaxAmountChange}
                                placeholder="Amount"
                                InputProps={{ 
                                  startAdornment: (
                                    <InputAdornment position="start">{currencySymbol}</InputAdornment>
                                  ),
                                }}
                                sx={amountFieldSx}
                              />
                              <DecimalTextField
                                size="small"
                                numericValue={taxPercent}
                                onNumericChange={handleTaxPercentChange}
                                placeholder="-"
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">%</InputAdornment>
                                  ),
                                }}
                                sx={percentFieldSx}
                              />
                            </Box>
                          )}
                        </TableCell>

                        {/* Net Amt. – show as plain text */}
                        <TableCell
                          sx={{
                            width: 140,
                            borderTopRightRadius:
                              onRemoveService && (svc.isFromSearch || svc.is_from_consumption) ? 0 : 12,
                            borderBottomRightRadius:
                              onRemoveService && (svc.isFromSearch || svc.is_from_consumption) ? 0 : 12,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              textAlign: 'left',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {formatCurrency(netAmount)}
                          </Typography>
                        </TableCell>
                        
                        {onRemoveService && (svc.isFromSearch || svc.is_from_consumption) && (
                          <TableCell
                            sx={{
                              borderTopRightRadius: 12,
                              borderBottomRightRadius: 12,
                              width: 64,
                            }}
                          >
                            <Tooltip title="Remove">
                              <IconButton
                                size="small"
                                onClick={() => onRemoveService(idx)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            )}
          />
        </Table>
      </TableContainer>
    </Card>
  );
};

export default ServicesList;
